import { yupResolver } from '@hookform/resolvers/yup';
import { Panel, PanelAnchor, Select } from '@insify/design-system/components';
import { FormContainer, activityQuestionIconMapping, useWizard } from '@insify/funnel-components';
import { isDefined } from '@insify/ui-utils';
import { ContentContextWrapper } from '@objectiv/tracker-react';
import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { discountQuestionKeys } from '../../../util/activitySpecificQuestions';
import { useCommands } from '../../hooks/useCommands';
import { useContent } from '../../hooks/useContent';
import { useConditionsQuery, useHighCareConditions, useParamsQuery } from '../../hooks/useQueries';

import * as styles from './Discount.module.css';
import { validation } from './validation';

import type { ActivitySpecificFieldDescription, Params } from '../../models/queries';
import type { InputSelectProps } from '@insify/components';
import type { HighestFrenchDegree } from '@insify/shared-company-policy-model';
import type { DeepPartial, SubmitHandler } from 'react-hook-form';

type FormType = DeepPartial<{
  activitySpecificFieldAnswers: Params['activitySpecificFieldAnswers'];
}>;

const highestDegreeOptions: InputSelectProps<HighestFrenchDegree>['options'] = [
  {
    label: 'CAP, BEP',
    value: 'capOrBep',
  },
  {
    label: 'Baccalauréat',
    value: 'baccalaureat',
  },
  {
    label: 'DEUG, BTS, DUEST, DUT',
    value: 'deugOrBtsOrDeustOrDut',
  },
  {
    label: 'Licence ou Licence professionnelle, BUT',
    value: 'licence',
  },
  {
    label: 'Maîtrise, Master ou équivalent',
    value: 'master',
  },
  {
    label: 'Certificat professionnel',
    value: 'professionalCertificate',
  },
  {
    label: 'Aucun des diplômes ci-dessus',
    value: 'noneOfTheAbove',
  },
];

const Inputs = ({ questions, submitting }: { questions: ActivitySpecificFieldDescription[]; submitting: boolean }) => {
  const formMethods = useFormContext<FormType>();
  const { t } = useTranslation(['funnel']);
  const relevantDegreeToggled = useWatch<FormType>({
    name: 'activitySpecificFieldAnswers.relevantDegree',
  });

  return (
    <>
      {questions.map(({ key, text, detailedText }) => {
        const description = (
          <>
            <span dangerouslySetInnerHTML={{ __html: text }} />
            {detailedText && detailedText.length > 0 && (
              <>
                {' '}
                <PanelAnchor data-testid={`readMore-${key}`}>
                  {t('funnel:activity_specific_questions_read_more')}
                </PanelAnchor>
              </>
            )}
          </>
        );
        return (
          <ContentContextWrapper key={key} id={key}>
            <Panel
              className={styles.panel}
              id={`activitySpecificFieldAnswers.${key}`}
              icon={activityQuestionIconMapping[key]}
              trackingId='toggle'
              aria-label={text}
              disabled={submitting}
              {...formMethods.register(`activitySpecificFieldAnswers.${key}`)}
              description={description}
              details={detailedText && <span dangerouslySetInnerHTML={{ __html: detailedText }}></span>}
            />
            {key === 'relevantDegree' && relevantDegreeToggled && (
              <Select
                className={styles.select}
                trackingId='highestDegree'
                {...formMethods.register('activitySpecificFieldAnswers.highestRelevantDegree')}
                error={formMethods.formState.errors.activitySpecificFieldAnswers?.highestRelevantDegree?.message}
                id='highestDegree'
                label={t('funnel:discount_highest_degree_label')}
                placeholder={t('funnel:discount_highest_degree_placeholder')}
                required={true}
              >
                {highestDegreeOptions.map(({ label, value }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          </ContentContextWrapper>
        );
      })}
    </>
  );
};

export function DiscountPage(): JSX.Element | null {
  const { showPreviousPage, showNextPage } = useWizard();
  const { t } = useTranslation(['common', 'funnel', 'validation']);
  const { quoteWizardDiscountQuestions } = useContent() || {};
  const { sendCommand } = useCommands();
  const { isStale: paramsStale, data: quoteParams, isFetched } = useParamsQuery();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [triggerShowingNextPage, setTriggerShowingNextPage] = useState(false);
  const { data: quoteConditions } = useConditionsQuery();
  const questions = discountQuestionKeys
    .map((key) => {
      return quoteConditions?.activitySpecificFieldDescriptions?.find(({ key: k }) => k === key);
    })
    .filter(isDefined);
  const { isStale: highCareConditionsStale } = useHighCareConditions();

  const handleCommand: SubmitHandler<FormType> = async ({ activitySpecificFieldAnswers }) => {
    setIsSubmitting(true);
    if (activitySpecificFieldAnswers) {
      if (!activitySpecificFieldAnswers.relevantDegree) {
        activitySpecificFieldAnswers.highestRelevantDegree = undefined;
      }
      await sendCommand({
        type: 'confirmActivitySpecificFields',
        payload: activitySpecificFieldAnswers,
      });
    }

    setTriggerShowingNextPage(true);
  };

  const hasCompletedDiscountStep =
    triggerShowingNextPage &&
    !paramsStale &&
    questions.some(({ key }) => key in (quoteParams?.activitySpecificFieldAnswers || {})) &&
    !highCareConditionsStale;

  useEffect(() => {
    if (hasCompletedDiscountStep) {
      showNextPage();
      setTriggerShowingNextPage(false);
    }
  }, [hasCompletedDiscountStep]);

  if (!isFetched || !quoteParams) {
    return null;
  }

  return (
    <FormContainer
      formData={quoteParams}
      inputs={<Inputs questions={questions} submitting={isSubmitting} />}
      validation={yupResolver(validation(t('validation:highest_degree_required')))}
      eventHandlers={{
        onSubmit: handleCommand,
        onBack: showPreviousPage,
      }}
      content={quoteWizardDiscountQuestions}
      translations={{
        back: t('common:back'),
        forward: t('common:continue'),
      }}
      submitting={isSubmitting}
    />
  );
}
